import {
  List,
  Datagrid,
  DateField,
  DateInput,
  FunctionField,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  useRecordContext,
} from 'react-admin';
import { Box, Chip, Typography } from '@mui/material';
import type { AuditEntry, AuditStatus } from '../../types';

// Feature 013: who changed what through the MCP server. Read-only.

const auditFilters = [
  <ReferenceInput key="clientId" source="clientId" reference="clients" alwaysOn>
    <SelectInput optionText="name" label="Client" />
  </ReferenceInput>,
  <TextInput key="user" source="user" label="User (email or ID)" alwaysOn />,
  <TextInput key="action" source="action" label="Action" />,
  <SelectInput
    key="status"
    source="status"
    choices={[
      { id: 'success', name: 'Success' },
      { id: 'denied', name: 'Denied' },
      { id: 'failed', name: 'Failed' },
    ]}
  />,
  <SelectInput
    key="source"
    source="source"
    choices={[
      { id: 'mcp', name: 'MCP' },
      { id: 'dashboard', name: 'Dashboard' },
    ]}
  />,
  <DateInput key="since" source="since" label="From" />,
  <DateInput key="until" source="until" label="To" />,
];

const statusColors: Record<AuditStatus, 'success' | 'warning' | 'error'> = {
  success: 'success',
  denied: 'warning',
  failed: 'error',
};

const StatusField = () => {
  const record = useRecordContext<AuditEntry>();
  if (!record) return null;
  return <Chip size="small" label={record.status} color={statusColors[record.status]} />;
};

const AuditDetails = () => {
  const record = useRecordContext<AuditEntry>();
  if (!record) return null;
  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2">
        <b>Changed fields:</b> {record.changedFields?.length ? record.changedFields.join(', ') : '—'}
      </Typography>
      <Typography variant="body2">
        <b>HTTP status:</b> {record.httpStatus}
        {record.buildId && (
          <>
            {' · '}
            <b>Build:</b> <a href={`#/builds/${record.buildId}/show`}>{record.buildId}</a>
          </>
        )}
      </Typography>
      {record.error && (
        <Typography variant="body2" color="error">
          <b>Error:</b> {record.error}
        </Typography>
      )}
      {record.details && (
        <Box component="pre" sx={{ m: 0, mt: 1, fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(record.details, null, 2)}
        </Box>
      )}
    </Box>
  );
};

export const AuditList = () => (
  <List filters={auditFilters} sort={{ field: 'createdAt', order: 'DESC' }} perPage={50} exporter={false}>
    <Datagrid bulkActionButtons={false} expand={<AuditDetails />} rowClick="expand">
      <DateField source="createdAt" label="Time" showTime />
      <FunctionField<AuditEntry> label="User" render={(r) => r.userEmail || r.userId} />
      <TextField source="source" />
      <TextField source="action" />
      <ReferenceField source="clientId" reference="clients" label="Client" emptyText="—" link="show">
        <TextField source="name" />
      </ReferenceField>
      <StatusField />
    </Datagrid>
  </List>
);
